import type {
  HsModelSceneParticipationView,
  HsModelView,
  HsNodeCreateInfo,
  HsNodeTransformView,
  HsNodeView,
  HsSceneGeometryReuseView,
  HsSceneModelInstanceInfo,
  HsSceneModelInstanceView,
  HsSceneSettings,
  HsSceneView,
  HsSimilarityRunView,
  HsTransformInfo,
  HsTransport,
  HsTransportRequest,
  HsTransportResponse,
} from '@sibe/hyperscene';
import { hsEnsureOk } from '../../../shared/http/api-error.function';

const JSON_CONTENT_TYPE: string = 'application/json';
// POST and PUT calls without a body still announce JSON; the API binds them as JSON actions.
// eslint-disable-next-line @typescript-eslint/naming-convention
const JSON_HEADERS: Readonly<Record<string, string>> = { 'Content-Type': JSON_CONTENT_TYPE };

export interface HsCatalogApiOptions {
  transport: HsTransport;
}

/** Models, scenes and their composition: listing, metadata, instances, transforms and similarity reruns. */
export class HsCatalogApi {
  private readonly transport: HsTransport;

  public constructor(options: HsCatalogApiOptions) {
    this.transport = options.transport;
  }

  /** Models whose metadata matches all `metadata` entries. */
  public filterModels(metadata: unknown[]): Promise<HsModelView[]> {
    return this.json<HsModelView[]>({
      path: 'api/Models/Filter',
      method: 'POST',
      body: { metadata, nodeIds: [], modelIds: [], isFullConformity: true },
    });
  }

  public getModelRootNode(modelId: string): Promise<HsNodeView> {
    return this.json<HsNodeView>({ path: `api/Models/${id(modelId)}/RootNode` });
  }

  /** Scenes that use the model, as an original or as instances. */
  public async getModelScenes(modelId: string): Promise<HsModelSceneParticipationView[]> {
    const scenes: HsModelSceneParticipationView[] = await this.json<HsModelSceneParticipationView[]>({
      path: `api/Models/${id(modelId)}/Scenes`,
    });
    // Older APIs omit the count; it equals the number of instance roots.
    return scenes.map((scene: HsModelSceneParticipationView) => ({
      ...scene,
      instanceCount: scene.instanceCount ?? scene.instanceRootNodeIds.length,
    }));
  }

  public deleteModel(modelId: string): Promise<void> {
    return this.noContent({ path: `api/Models/${id(modelId)}`, method: 'DELETE', headers: JSON_HEADERS });
  }

  public putModelMetadata(modelId: string, metadata: unknown[]): Promise<HsModelView> {
    return this.json<HsModelView>({ path: `api/Models/${id(modelId)}/Metadata`, method: 'PUT', body: metadata });
  }

  public getModelSimilarityRuns(modelId: string): Promise<HsSimilarityRunView[]> {
    return this.json<HsSimilarityRunView[]>({ path: `api/Models/${id(modelId)}/SimilarityRuns` });
  }

  /**
   * Starts a similarity search over the whole model and returns its processing id. With
   * `restoreExistingReplacements`, earlier replacements are undone first (a full rerun).
   */
  public rerunModelSimilarity(
    modelId: string,
    options: { restoreExistingReplacements?: boolean } = {}
  ): Promise<string> {
    return this.json<string>({
      path: 'api/Geometries/Similar/Find',
      method: 'POST',
      body: { modelId, meshIds: null, restoreExistingReplacements: options.restoreExistingReplacements ?? false },
    });
  }

  public createScene(metadata: unknown[]): Promise<HsSceneView> {
    return this.json<HsSceneView>({ path: 'api/Scenes', method: 'POST', body: metadata });
  }

  public filterScenes(metadata: unknown[]): Promise<HsSceneView[]> {
    return this.json<HsSceneView[]>({ path: 'api/Scenes/Filter', method: 'POST', body: metadata });
  }

  public putSceneMetadata(sceneId: string, metadata: unknown[]): Promise<HsSceneView> {
    return this.json<HsSceneView>({ path: `api/Scenes/${id(sceneId)}/Metadata`, method: 'PUT', body: metadata });
  }

  /** Replaces the scene's viewer defaults; a missing or null field clears that default. */
  public putSceneSettings(sceneId: string, settings: HsSceneSettings): Promise<HsSceneView> {
    return this.json<HsSceneView>({
      path: `api/Scenes/${id(sceneId)}/Settings`,
      method: 'PUT',
      body: { startCamera: settings.startCamera ?? null, qualityProfile: settings.qualityProfile ?? null },
    });
  }

  public deleteScene(sceneId: string): Promise<void> {
    return this.noContent({ path: `api/Scenes/${id(sceneId)}`, method: 'DELETE', headers: JSON_HEADERS });
  }

  /** Reuses identical geometry across the scene's models incrementally. */
  public rerunSceneGeometryReuse(sceneId: string): Promise<HsSceneGeometryReuseView> {
    return this.json<HsSceneGeometryReuseView>({
      path: `api/Scenes/${id(sceneId)}/GeometryReuse`,
      method: 'POST',
      body: {},
    });
  }

  /** Adds a model's node tree to the scene as an original. */
  public addNodeTreeToScene(sceneId: string, rootNodeId: string): Promise<void> {
    return this.noContent({
      path: `api/Scenes/${id(sceneId)}/Nodes/${id(rootNodeId)}`,
      method: 'PUT',
      headers: JSON_HEADERS,
    });
  }

  /** Removes node trees from the scene; `force` also removes trees other scene nodes depend on. */
  public removeNodeTreesFromScene(
    sceneId: string,
    rootNodeIds: readonly string[],
    force: boolean = false
  ): Promise<void> {
    return this.noContent({
      path: `api/Scenes/${id(sceneId)}/Nodes`,
      method: 'DELETE',
      query: { force: force ? true : undefined },
      body: rootNodeIds,
    });
  }

  public createSceneModelInstance(sceneId: string, info: HsSceneModelInstanceInfo): Promise<HsNodeView> {
    return this.json<HsNodeView>({ path: `api/Scenes/${id(sceneId)}/ModelInstances`, method: 'POST', body: info });
  }

  /** The instance record of a scene root, or null when the root is an original rather than an instance. */
  public async getSceneModelInstance(sceneId: string, rootNodeId: string): Promise<HsSceneModelInstanceView | null> {
    const response: HsTransportResponse = await this.transport.request({
      path: `api/Scenes/${id(sceneId)}/ModelInstances/${id(rootNodeId)}`,
      accept: [JSON_CONTENT_TYPE],
    });
    if (response.status === 404) {
      return null;
    }
    return (await hsEnsureOk(response)).json<HsSceneModelInstanceView>();
  }

  public transformSceneModelInstance(
    sceneId: string,
    rootNodeId: string,
    transform: HsTransformInfo
  ): Promise<HsNodeView> {
    return this.json<HsNodeView>({
      path: `api/Scenes/${id(sceneId)}/ModelInstances/${id(rootNodeId)}/Transform`,
      method: 'PUT',
      body: transform,
    });
  }

  public createNode(info: HsNodeCreateInfo): Promise<HsNodeView> {
    return this.json<HsNodeView>({ path: 'api/Nodes', method: 'POST', body: info });
  }

  public deleteNode(nodeId: string): Promise<void> {
    return this.noContent({ path: `api/Nodes/${id(nodeId)}`, method: 'DELETE', headers: JSON_HEADERS });
  }

  public getNodeTransform(nodeId: string): Promise<HsNodeTransformView> {
    return this.json<HsNodeTransformView>({ path: `api/Nodes/${id(nodeId)}/Transform` });
  }

  public transformNodes(transforms: readonly { nodeId: string; transformInfo: HsTransformInfo }[]): Promise<void> {
    return this.noContent({ path: 'api/Nodes/Transform/Bulk', method: 'PUT', body: transforms });
  }

  private async json<T>(request: HsTransportRequest): Promise<T> {
    return (await hsEnsureOk(await this.transport.request({ ...request, accept: [JSON_CONTENT_TYPE] }))).json<T>();
  }

  private async noContent(request: HsTransportRequest): Promise<void> {
    await hsEnsureOk(await this.transport.request({ ...request, accept: [JSON_CONTENT_TYPE] }));
  }
}

function id(value: string): string {
  return encodeURIComponent(value);
}
